import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useWorkspace } from 'hooks/useWorkspace';

import arrow from 'assets/arrow.svg';
import glyphWaves from 'assets/glyph_waves.webm';
import imageUploadIcon from 'assets/image_upload_icon.png';
import spinner from 'assets/remix-spinner.gif';
import removeFrameIcon from 'assets/remove_frame_icon.svg';
import videoUploadIcon from 'assets/video_upload_icon.png';

import { MAX_UPLOAD_FILES } from 'Pages/TeamDashboard/CreateReview/components/constants';
import useScreenUpload, { VIDEO_PHASES } from 'Pages/TeamDashboard/CreateReview/hooks/useScreenUpload';
import FilledState from 'Pages/TeamDashboard/ProductLibrary/components/FilledState';
import LoadingState from 'Pages/TeamDashboard/ProductLibrary/components/LoadingState';

import { fetchProductLibraryAction, saveFlowAction } from 'Redux/Actions/productLibraryActions';

import FilesService from 'services/files';
import { classifyFlowImages } from 'services/productLibrary';

import { FLOW_SOURCE } from 'utlis/constants';
import { errorReactToastify } from 'utlis/toasts';

import styles from './styles.module.scss';

const CLASSIFY_PHASE = {
  IDLE: 'idle',
  CLASSIFYING: 'classifying',
  CLASSIFIED: 'classified',
  FAILED: 'failed',
};

const ACCEPTED_VIDEO_EXTENSIONS = '.mp4,.mov,.webm';

const ProductLibrarySelection = ({ onBack, onConfirm, selectedLibraryFlowUuid, onLibraryFlowSelect }) => {
  const dispatch = useDispatch();
  const { selectedWorkspace } = useWorkspace();
  const { categories, isLoading, isEmpty } = useSelector(state => state.productLibrary);

  const [selectedVersions, setSelectedVersions] = useState({});

  // Upload flow state (used when library is empty)
  const [uploadedImages, setUploadedImages] = useState([]);
  const [classifyPhase, setClassifyPhase] = useState(CLASSIFY_PHASE.IDLE);
  const [classifyResult, setClassifyResult] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const imageFilesRef = useRef([]);
  const classifyAbortRef = useRef(null);

  const handleSetOriginalFiles = useCallback(files => {
    imageFilesRef.current = files;
  }, []);

  const {
    fileInputRef,
    videoFileInputRef,
    videoPhase,
    isDragging,
    triggerFileInput,
    triggerVideoFileInput,
    handleFileSelect,
    handleVideoFileSelect,
    handleVideoRetry,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    removeImage,
    clearAll,
    isVideoActive,
  } = useScreenUpload({
    uploadedImages,
    setUploadedImages,
    setOriginalFiles: handleSetOriginalFiles,
  });

  useEffect(() => {
    dispatch(fetchProductLibraryAction(selectedWorkspace?.uuid));
  }, [selectedWorkspace?.uuid, dispatch]);

  // Auto-classify uploaded images
  useEffect(() => {
    if (classifyAbortRef.current) {
      classifyAbortRef.current.abort();
      classifyAbortRef.current = null;
    }

    if (uploadedImages.length === 0) {
      setClassifyPhase(CLASSIFY_PHASE.IDLE);
      setClassifyResult(null);

      return;
    }

    const controller = new AbortController();

    classifyAbortRef.current = controller;

    const doClassify = async () => {
      setClassifyPhase(CLASSIFY_PHASE.CLASSIFYING);

      try {
        const files = await Promise.all(
          uploadedImages.map(async (img, index) => {
            if (controller.signal.aborted) {
              throw new DOMException('Aborted', 'AbortError');
            }

            const existingFile = imageFilesRef.current[index];

            if (existingFile) {
              return existingFile;
            }

            const response = await fetch(img.url, { signal: controller.signal });
            const blob = await response.blob();
            const ext = (blob.type.split('/')[1] || 'jpg').split('+')[0];

            return new File([blob], `frame_${index + 1}.${ext}`, { type: blob.type });
          })
        );

        if (controller.signal.aborted) {
          return;
        }

        const MAX_RETRIES = 3;
        let result;

        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          if (controller.signal.aborted) {
            return;
          }

          try {
            result = await classifyFlowImages(files, controller.signal);
            break;
          }
          catch (err) {
            if (err.name === 'AbortError') {
              throw err;
            }

            if (attempt < MAX_RETRIES - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            }
            else {
              throw err;
            }
          }
        }

        if (controller.signal.aborted) {
          return;
        }

        const categoryName = result.category || result.label?.category || '';
        const subFlowName = result.sub_flow || result.flow || result.label?.sub_flow || '';

        setClassifyResult({ category_name: categoryName, sub_flow: subFlowName });
        setClassifyPhase(CLASSIFY_PHASE.CLASSIFIED);
      }
      catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        setClassifyPhase(CLASSIFY_PHASE.FAILED);
      }
    };

    doClassify();

    return () => {
      controller.abort();
    };
  }, [uploadedImages]);

  const handleClearScreens = useCallback(() => {
    clearAll();
    imageFilesRef.current = [];
    setClassifyResult(null);
    setClassifyPhase(CLASSIFY_PHASE.IDLE);
  }, [clearAll]);

  const handleDragEnd = useCallback(result => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    setUploadedImages(prev => {
      const items = [...prev];
      const [reorderedItem] = items.splice(sourceIndex, 1);

      items.splice(destinationIndex, 0, reorderedItem);

      return items;
    });

    if (imageFilesRef.current && imageFilesRef.current.length > 0) {
      const files = [...imageFilesRef.current];
      const [reorderedFile] = files.splice(sourceIndex, 1);

      files.splice(destinationIndex, 0, reorderedFile);
      imageFilesRef.current = files;
    }

    setClassifyResult(null);
    setClassifyPhase(CLASSIFY_PHASE.IDLE);
  }, []);

  const handleSaveAndContinue = useCallback(async () => {
    const finalCategory = classifyResult?.category_name;
    const finalSubFlow = classifyResult?.sub_flow;

    if (isSaving || uploadedImages.length === 0 || !finalCategory || !finalSubFlow) {
      return;
    }

    setIsSaving(true);

    try {
      const hasVideo = uploadedImages.some(img => img.isVideoFrame);
      const source = hasVideo ? FLOW_SOURCE.VIDEO : FLOW_SOURCE.SCREENSHOT;

      let imageUrls;

      if (source === FLOW_SOURCE.SCREENSHOT) {
        const uploadWithRetry = async (file, maxRetries = 3) => {
          let lastError;

          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
              const url = await FilesService.uploadFileFromLocal(file);

              return url;
            }
            catch (error) {
              lastError = error;

              if (attempt < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, attempt * 1000));
              }
            }
          }

          throw lastError;
        };

        try {
          imageUrls = await Promise.all(
            imageFilesRef.current.map(file => uploadWithRetry(file))
          );
        }
        catch (_uploadError) {
          errorReactToastify('Failed to upload images. Please check your connection and try again.', {
            autoClose: 5000,
            position: 'bottom-center',
            hideProgressBar: true,
            style: { backgroundColor: '#FDF0F0', width: '470px' },
          });

          return;
        }
      }
      else {
        imageUrls = uploadedImages.map(img => img.url);
      }

      const assets = imageUrls.map((url, i) => ({ image_url: url, order: i + 1 }));

      const savedFlow = await dispatch(
        saveFlowAction(selectedWorkspace?.uuid, {
          name: finalSubFlow,
          source,
          category_name: finalCategory,
          assets,
        })
      );

      const libraryImages = imageUrls.map((url, index) => ({
        uid: `library-new-${index}`,
        url,
        preview: url,
        isLibraryImage: true,
        progress: 100,
        isProgressLoading: false,
      }));

      onConfirm({
        uuid: savedFlow?.uuid,
        name: finalSubFlow,
        category_name: finalCategory,
        current_version: 1,
        selected_version: 1,
        assets: libraryImages.map((img, i) => ({ image_url: img.url, order: i + 1 })),
      }, libraryImages);
    }
    catch (_err) {
      errorReactToastify('Failed to save flow. Please try again.', {
        autoClose: 5000,
        position: 'bottom-center',
        hideProgressBar: true,
        style: { backgroundColor: '#FDF0F0', width: '470px' },
      });
    }
    finally {
      setIsSaving(false);
    }
  }, [classifyResult, dispatch, isSaving, onConfirm, selectedWorkspace?.uuid, uploadedImages]);

  // Filled state (existing flow) logic
  const selectedFlow = useMemo(() => {
    if (!selectedLibraryFlowUuid) {
      return null;
    }

    for (const category of categories) {
      const found = category.flows.find(f => f.uuid === selectedLibraryFlowUuid);

      if (found) {
        return found;
      }
    }

    return null;
  }, [categories, selectedLibraryFlowUuid]);

  const handleConfirm = useCallback(() => {
    if (selectedFlow) {
      // Get the selected version for this flow, or use current_version
      const selectedVersion = selectedVersions[selectedFlow.uuid] || selectedFlow.current_version;

      // Pass flow with selected version info
      onConfirm({
        ...selectedFlow,
        selected_version: selectedVersion,
      });
    }
  }, [onConfirm, selectedFlow, selectedVersions]);

  const handleVersionSelect = useCallback((flowUuid, versionNumber) => {
    setSelectedVersions(prev => ({
      ...prev,
      [flowUuid]: versionNumber,
    }));
  }, []);

  const isSaveDisabled = isSaving
    || uploadedImages.length === 0
    || classifyPhase === CLASSIFY_PHASE.CLASSIFYING
    || !classifyResult?.category_name
    || !classifyResult?.sub_flow;

  const showScreensUploaded = uploadedImages.length > 0 && !isVideoActive;
  const showUploadOptions = uploadedImages.length === 0 && !isVideoActive;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {isLoading && <LoadingState />}

        {!isLoading && isEmpty && (
          <div className={styles.uploadFlowWrapper}>
            <h2 className={styles.uploadTitle}>
              Select your screens
            </h2>

            {showUploadOptions && (
              <div
                className={`${styles.dropZone} ${isDragging ? styles.dropZoneDragging : ''}`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {/* Hidden file inputs */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <input
                  ref={videoFileInputRef}
                  type="file"
                  accept={ACCEPTED_VIDEO_EXTENSIONS}
                  onChange={handleVideoFileSelect}
                  className="hidden"
                />

                <p className={styles.dropZoneTitle}>
                  No screens yet
                </p>
                <p className={styles.dropZoneSubtitle}>
                  Upload a screenshot or screen recording and we&apos;ll turn it
                  into your first product flow in just a few minutes.
                </p>

                <div className={styles.uploadCard}>
                  <button type="button" onClick={triggerVideoFileInput} className={styles.uploadCardButton}>
                    <img src={videoUploadIcon} alt="" className="w-16 h-16" />
                    <span className={styles.uploadCardLabel}>
                      Upload screen recording
                    </span>
                  </button>
                  <div className={styles.uploadCardDivider} />
                  <button type="button" onClick={triggerFileInput} className={styles.uploadCardButton}>
                    <span className={styles.uploadCardMaxLabel}>
                      {`Max. ${MAX_UPLOAD_FILES}`}
                    </span>
                    <img src={imageUploadIcon} alt="" className="w-16 h-16" />
                    <span className={styles.uploadCardLabel}>
                      or screenshots
                    </span>
                  </button>
                </div>
              </div>
            )}

            {(videoPhase === VIDEO_PHASES.UPLOADING || videoPhase === VIDEO_PHASES.PROCESSING) && (
              <div className={styles.dropZone}>
                <div className={styles.videoOverlay}>
                  <video
                    src={glyphWaves}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className={styles.videoBackground}
                  />
                  <p className={styles.videoMessage}>
                    {videoPhase === VIDEO_PHASES.UPLOADING && 'Uploading your video...'}
                    {videoPhase === VIDEO_PHASES.PROCESSING && 'Loading your designs...'}
                  </p>
                </div>
              </div>
            )}

            {videoPhase === VIDEO_PHASES.FAILED && (
              <div className={styles.dropZone}>
                <div className={styles.videoFailed}>
                  <p className="text-sm text-red-500">
                    Processing failed
                  </p>
                  <button type="button" className={styles.retryButton} onClick={handleVideoRetry}>
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {showScreensUploaded && (
              <div className={styles.screensUploadedArea}>
                <div className={styles.classifyStatus}>
                  {classifyPhase === CLASSIFY_PHASE.CLASSIFYING && (
                    <div className={styles.classifyLoading}>
                      <img src={spinner} alt="Classifying" width={20} height={20} />
                      Classifying your flow...
                    </div>
                  )}

                  {classifyPhase === CLASSIFY_PHASE.CLASSIFIED && classifyResult && (
                    <div className={styles.classifyLabels}>
                      <span className={styles.categoryLabel}>
                        {classifyResult.category_name}
                      </span>
                      <span className={styles.subFlowLabel}>
                        {classifyResult.sub_flow}
                      </span>
                    </div>
                  )}

                  {classifyPhase === CLASSIFY_PHASE.FAILED && (
                    <div className={styles.classifyLabels}>
                      <span className={styles.categoryLabel}>
                        Uncategorized
                      </span>
                    </div>
                  )}
                </div>

                <div className={styles.screensRowWrapper}>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="screens" direction="horizontal">
                      {provided => (
                        <div
                          className={styles.screensRow}
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {uploadedImages.map((img, index) => {
                            const uid = img.uid || `img-${index}`;

                            return (
                              <Draggable key={uid} draggableId={String(uid)} index={index}>
                                {(given, snapshot) => (
                                  <div
                                    ref={given.innerRef}
                                    {...given.draggableProps}
                                    {...given.dragHandleProps}
                                    className={`${styles.screenItem}
                                      ${snapshot.isDragging ? styles.screenItemDragging : ''}`}
                                  >
                                    <div className={styles.screenImageWrapper}>
                                      <img
                                        src={img.preview || img.url}
                                        alt={`Screen ${index + 1}`}
                                        className={styles.screenImage}
                                      />
                                    </div>
                                    <button
                                      type="button"
                                      className={styles.removeBtn}
                                      onClick={e => {
                                        e.stopPropagation();
                                        removeImage(img.uid);
                                      }}
                                      aria-label={`Remove screen ${index + 1}`}
                                    >
                                      <img src={removeFrameIcon} alt="" width={30} height={30} />
                                    </button>
                                    <span className={styles.screenNumber}>
                                      {index + 1}
                                    </span>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </div>
            )}
          </div>
        )}

        {!isLoading && !isEmpty && (
          <FilledState
            categories={categories}
            selectionMode
            selectedLibraryFlowUuid={selectedLibraryFlowUuid}
            onLibraryFlowSelect={onLibraryFlowSelect}
            screenCount={selectedFlow?.screen_count ?? 0}
            onVersionSelectExternal={handleVersionSelect}
            selectedVersionsExternal={selectedVersions}
          />
        )}
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.backBtn} onClick={onBack}>
          Back
        </button>

        {isEmpty && showScreensUploaded && (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={handleClearScreens}
          >
            Clear screens
          </button>
        )}

        {isEmpty
          ? (
            <button
              type="button"
              className={isSaveDisabled ? styles.confirmBtnDisabled : styles.confirmBtn}
              onClick={handleSaveAndContinue}
              disabled={isSaveDisabled}
            >
              {isSaving ? 'Saving...' : (isSaveDisabled ? 'Add your context' : 'Save and add your context')}
              {!isSaving && <img src={arrow} alt="" />}
            </button>
          )
          : (
            <button
              type="button"
              className={styles.confirmBtn}
              onClick={handleConfirm}
              disabled={!selectedFlow}
            >
              Add your context
              <img src={arrow} alt="" />
            </button>
          )}
      </div>
    </div>
  );
};

ProductLibrarySelection.propTypes = {
  onBack: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  selectedLibraryFlowUuid: PropTypes.string,
  onLibraryFlowSelect: PropTypes.func.isRequired,
};

ProductLibrarySelection.defaultProps = {
  selectedLibraryFlowUuid: null,
};

export default ProductLibrarySelection;
